import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '~/styles/extended-theme.ts';

const buttonVariants = cva(
  'rounded-full inline-flex items-center justify-center text-sm font-medium font-mono uppercase ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/75 disabled:bg-primary/35 disabled:text-primary/75 focus-visible:bg-primary/75',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/75 focus-visible:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-primary-light/25 hover:text-accent-background focus-visible:bg-primary-light/50',
        'outline-secondary':
          'border border-destructive text-destructive bg-background hover:bg-secondary/10 focus-visible:bg-secondary/30',
        'outline-inverted':
          'border border-white text-background hover:bg-primary-light/25 hover:text-accent-background focus-visible:bg-primary-light/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus-visible:bg-secondary/90',
        ghost:
          'text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-0 focus-visible:bg-primary/30',
        'ghost-secondary':
          'text-secondary hover:bg-secondary/10 focus-visible:outline-none focus-visible:ring-0 focus-visible:bg-secondary/30',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-3 text-button-small',
        lg: 'h-11 px-8',
        'icon-sm': 'h-6 w-6',
        icon: 'h-8 w-8',
        'icon-lg': 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
